<script setup lang="ts">
import type { YouTubePlayer } from '~kui/utils/youTubeIframeApi';

import Player from '@vimeo/player';
import { Icon } from '~ds/components/ui/icon';
import { ICONS } from '~ds/lib/icons';
import { useVideoProgressMilestones } from '~kui/composables/useVideoProgressMilestones';
import { parseVideoUrl, VIDEO_PROVIDER, videoEmbedUrl } from '~kui/utils/videoUrl';
import { loadYouTubeIframeApi } from '~kui/utils/youTubeIframeApi';

import type { VideoProgressMilestone } from './types';

import KUICoreUnsupportedVideoSource from './KUICoreUnsupportedVideoSource.vue';

interface Props {
  url: string;
  title: string;
  thumbnailUrl?: string | null;
}

const props = withDefaults(defineProps<Props>(), { thumbnailUrl: null });
const emit = defineEmits<{ play: []; progress: [percent: VideoProgressMilestone] }>();

const parsed = computed(() => parseVideoUrl(props.url));
const playing = ref(false);
const iframe = ref<HTMLIFrameElement | null>(null);
const embedSrc = computed(() => (parsed.value ? videoEmbedUrl(parsed.value, { autoplay: true, jsApi: true }) : ''));

// Vimeo's player; a YouTube video's is `youTubePlayer`, further down.
let player: Player | null = null;
const progress = useVideoProgressMilestones({ onMilestone: milestone => emit('progress', milestone) });

const listenForProgress = () => {
  if (parsed.value?.provider !== VIDEO_PROVIDER.Vimeo || !iframe.value) {
    return;
  }
  player = new Player(iframe.value);
  player.on('timeupdate', ({ percent }) => progress.report(percent));
  player.on('ended', () => progress.report(1));
};

// Only `destroy` removes the player's window listeners, but it also removes the iframe while that is
// still in the page, and the iframe is Vue's: so a player is `off`'d here and destroyed after Vue
// has taken its iframe out.
const stopListeningForProgress = () => {
  player?.off('timeupdate');
  player?.off('ended');
  player = null;
  progress.reset();
};

watch(() => props.url, async () => {
  const stopped = player;
  stopListeningForProgress();
  playing.value = false;
  await nextTick();
  void stopped?.destroy();
});

onUnmounted(() => {
  void player?.destroy();
  stopListeningForProgress();
});

const YOUTUBE_POLL_MS = 1000;

let youTubePlayer: YouTubePlayer | null = null;
let youTubePoll: ReturnType<typeof setInterval> | undefined;

const reportYouTubeProgress = (watching: YouTubePlayer) => {
  const duration = watching.getDuration();
  if (duration > 0) {
    progress.report(watching.getCurrentTime() / duration);
  }
};

// YouTube's player has no time event, so its position is polled for the same milestones Vimeo
// reports. Progress is best-effort: when the API script is blocked the video still plays.
const listenForYouTubeProgress = async () => {
  const element = iframe.value;
  if (parsed.value?.provider !== VIDEO_PROVIDER.YouTube || !element) {
    return;
  }
  const api = await loadYouTubeIframeApi().catch(() => null);
  if (!api || iframe.value !== element) {
    return;
  }
  youTubePlayer = new api.Player(element, {
    events: {
      onReady: ({ target }) => {
        if (target === youTubePlayer) {
          youTubePoll = setInterval(() => reportYouTubeProgress(target), YOUTUBE_POLL_MS);
        }
      },
      onStateChange: ({ target, data }) => {
        if (target === youTubePlayer && data === api.PlayerState.ENDED) {
          progress.report(1);
        }
      },
    },
  });
};

// `destroy` also removes the iframe, which is Vue's, so it waits until Vue has taken it out.
const stopListeningForYouTubeProgress = async () => {
  clearInterval(youTubePoll);
  const stopped = youTubePlayer;
  youTubePlayer = null;
  await nextTick();
  stopped?.destroy();
};

watch(() => props.url, stopListeningForYouTubeProgress);

onUnmounted(stopListeningForYouTubeProgress);

// The play button unmounts with the poster, so focus would fall back to <body>; move it onto
// the player that replaced it.
const play = async () => {
  playing.value = true;
  emit('play');
  await nextTick();
  iframe.value?.focus();
  listenForProgress();
  void listenForYouTubeProgress();
};
</script>

<template>
  <KUICoreUnsupportedVideoSource v-if="!parsed" />
  <div v-else class="relative aspect-video w-full overflow-hidden rounded-3xl bg-black">
    <iframe
      v-if="playing"
      ref="iframe"
      :src="embedSrc"
      :title="title"
      class="absolute inset-0 size-full"
      allow="autoplay; fullscreen; picture-in-picture"
      allowfullscreen
    />
    <template v-else>
      <!-- The poster is the page's LCP image: sized to the player box and fetched first. -->
      <NuxtImg
        v-if="thumbnailUrl"
        :src="thumbnailUrl"
        alt=""
        width="1280"
        height="720"
        sizes="100vw lg:1024px"
        fetchpriority="high"
        class="absolute inset-0 size-full object-cover"
      />
      <button
        type="button"
        data-testid="video-embed-play"
        :aria-label="$t('kui.video.play', { title })"
        class="group absolute inset-0 flex items-center justify-center focus-visible:outline-none"
        @click="play"
      >
        <!-- The button fills the clipped player box, so its own ring would be cut off; the circle
             carries it instead, black against the lime so it reads on any poster. -->
        <span
          class="flex size-20 items-center justify-center rounded-full bg-secondary text-secondary-foreground group-focus-visible:outline-3 group-focus-visible:outline-ring"
          data-testid="video-embed-play-badge"
        >
          <Icon :name="ICONS.play" size="xl" />
        </span>
      </button>
    </template>
  </div>
</template>
